import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/shared/auth/store';
import { NAV_EXPANDED_PREF, SIDEBAR_COLLAPSED_PREF, writePref } from '@/shared/prefs/storage';
import { login, logout } from './api';
import type { LoginRequest } from './types';

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: LoginRequest) => login(input),
    onSuccess: (response) => {
      setSession(response);
      // slice-3608 (Chris 2026-09-08 evening, correcting slice-3590 — "I meant to collapse the
      // navigation TREE completely, not the entire nav bar"): a fresh LOGIN starts with the
      // sidebar OPEN and every tree branch CLOSED. The Sidebar's active-route effect then opens
      // only the trail to wherever the operator navigates; their expand/collapse choices stick
      // for the rest of that session. Rehydrated sessions (page reload) are not new logins.
      // The sidebar-collapsed pref is reset to false so the 3590 behaviour (rail only) is undone
      // for everyone who logged in today.
      writePref(response.userId, SIDEBAR_COLLAPSED_PREF, false);
      writePref(response.userId, NAV_EXPANDED_PREF, {});
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const clear = useAuthStore((s) => s.clear);
  return useMutation({
    mutationFn: () => logout(),
    // Clear local state regardless of server response — the JWT is already stale on the
    // client's clock; if the server didn't get the invalidate it'll let the session expire.
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
}
